import React, { useState, useEffect, useContext, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Settings,
  Plus,
  Edit2,
  Trash2,
  RefreshCw,
  AlertCircle,
  Check,
  X,
  RotateCcw,
  Smile,
  GripVertical,
  Upload,
  Image,
} from "lucide-react";
import recognitionTypesService from "../../services/recognitionTypesService";
import organizationsService from "../../services/organizationsService";
import AuthContext from "../../context/AuthContext";
import useCustomToast from "../../hooks/useCustomToast";
import usePressDragReorder from "../../hooks/usePressDragReorder";
import { formatError } from "../../utils/errorHandler";
import { useTranslation } from "../../i18n";

// Predefined gradient options (easy presets). Labels are looked up by id.
const GRADIENT_PRESETS = [
  { from: "#3b82f6", to: "#06b6d4", id: "blue_cyan" },
  { from: "#22c55e", to: "#10b981", id: "green_emerald" },
  { from: "#eab308", to: "#f97316", id: "yellow_orange" },
  { from: "#a855f7", to: "#ec4899", id: "purple_pink" },
  { from: "#6366f1", to: "#3b82f6", id: "indigo_blue" },
  { from: "#f59e0b", to: "#eab308", id: "amber_yellow" },
  { from: "#ef4444", to: "#f43f5e", id: "red_rose" },
  { from: "#14b8a6", to: "#22c55e", id: "teal_green" },
  { from: "#ec4899", to: "#a855f7", id: "pink_purple" },
  { from: "#06b6d4", to: "#3b82f6", id: "cyan_blue" },
  { from: "#f97316", to: "#ef4444", id: "orange_red" },
  { from: "#84cc16", to: "#22c55e", id: "lime_green" },
];

// Color palette for visual selection
const COLOR_PALETTE = [
  // Reds
  ["#fecaca", "#fca5a5", "#f87171", "#ef4444", "#dc2626", "#b91c1c", "#991b1b"],
  // Oranges
  ["#fed7aa", "#fdba74", "#fb923c", "#f97316", "#ea580c", "#c2410c", "#9a3412"],
  // Yellows
  ["#fef08a", "#fde047", "#facc15", "#eab308", "#ca8a04", "#a16207", "#854d0e"],
  // Greens
  ["#bbf7d0", "#86efac", "#4ade80", "#22c55e", "#16a34a", "#15803d", "#166534"],
  // Teals
  ["#99f6e4", "#5eead4", "#2dd4bf", "#14b8a6", "#0d9488", "#0f766e", "#115e59"],
  // Blues
  ["#bfdbfe", "#93c5fd", "#60a5fa", "#3b82f6", "#2563eb", "#1d4ed8", "#1e40af"],
  // Indigos
  ["#c7d2fe", "#a5b4fc", "#818cf8", "#6366f1", "#4f46e5", "#4338ca", "#3730a3"],
  // Purples
  ["#e9d5ff", "#d8b4fe", "#c084fc", "#a855f7", "#9333ea", "#7e22ce", "#6b21a8"],
  // Pinks
  ["#fbcfe8", "#f9a8d4", "#f472b6", "#ec4899", "#db2777", "#be185d", "#9d174d"],
  // Grays
  ["#e5e7eb", "#d1d5db", "#9ca3af", "#6b7280", "#4b5563", "#374151", "#1f2937"],
];

// Extended emoji options organized by category. Labels are looked up by id.
const EMOJI_CATEGORIES = {
  people: ["🤝", "💪", "👏", "🙌", "👍", "✋", "🤲", "👑"],
  objects: ["💼", "💡", "🎯", "⭐", "🏆", "🎖️", "💎", "🔑"],
  celebration: ["🎉", "🎊", "✨", "🌟", "🔥", "💥", "⚡", "🎁"],
  hearts: ["❤️", "💜", "💙", "💚", "🧡", "💛", "🤍", "💖"],
  nature: ["🚀", "🌈", "☀️", "🌙", "🦁", "🦅", "🐝", "🌸"],
  sports: ["🥇", "🥈", "🥉", "🎮", "🎨", "🎵", "📚", "💻"],
};

// Quick access emojis (shown first)
const QUICK_EMOJIS = ["🤝", "💼", "💡", "👑", "🎯", "⭐", "🚀", "💪", "🏆", "🎉", "❤️", "🔥", "✨", "🌟", "💎", "🎖️"];

const RecognitionTypesSection = () => {
  const queryClient = useQueryClient();
  const toast = useCustomToast();
  const { t } = useTranslation("admin");
  const { user } = useContext(AuthContext);
  const isSuperAdmin = user?.is_superuser;

  const presetLabels = {
    blue_cyan: t("recognition_types.presets.blue_cyan"),
    green_emerald: t("recognition_types.presets.green_emerald"),
    yellow_orange: t("recognition_types.presets.yellow_orange"),
    purple_pink: t("recognition_types.presets.purple_pink"),
    indigo_blue: t("recognition_types.presets.indigo_blue"),
    amber_yellow: t("recognition_types.presets.amber_yellow"),
    red_rose: t("recognition_types.presets.red_rose"),
    teal_green: t("recognition_types.presets.teal_green"),
    pink_purple: t("recognition_types.presets.pink_purple"),
    cyan_blue: t("recognition_types.presets.cyan_blue"),
    orange_red: t("recognition_types.presets.orange_red"),
    lime_green: t("recognition_types.presets.lime_green"),
  };
  const emojiCategoryLabels = {
    people: t("recognition_types.emoji_categories.people"),
    objects: t("recognition_types.emoji_categories.objects"),
    celebration: t("recognition_types.emoji_categories.celebration"),
    hearts: t("recognition_types.emoji_categories.hearts"),
    nature: t("recognition_types.emoji_categories.nature"),
    sports: t("recognition_types.emoji_categories.sports"),
  };

  const [selectedOrgId, setSelectedOrgId] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editingType, setEditingType] = useState(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [iconTab, setIconTab] = useState("emojis"); // "emojis" or "custom"

  // Form state - using hex colors directly for simplicity
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    icon: "⭐",
    color_from: "#a855f7",
    color_to: "#3b82f6",
  });

  // Track which color is being picked (null, 'from', or 'to')
  const [pickingColor, setPickingColor] = useState(null);

  // Fetch organizations for the dropdown
  const { data: organizations = [], isLoading: isLoadingOrgs } = useQuery({
    queryKey: ["organizations"],
    queryFn: () => organizationsService.getAllOrganizations(),
  });

  // Set org: use user's org for org admins, first org for super admins
  useEffect(() => {
    if (!selectedOrgId) {
      if (!isSuperAdmin && user?.organization_id) {
        setSelectedOrgId(user.organization_id);
      } else if (organizations.length > 0) {
        setSelectedOrgId(organizations[0].id);
      }
    }
  }, [organizations, selectedOrgId, isSuperAdmin, user?.organization_id]);

  // Fetch recognition types for selected organization
  const {
    data: allRecognitionTypes = [],
    isLoading: isLoadingTypes,
    refetch: refetchTypes,
    isFetched: isTypesFetched,
  } = useQuery({
    queryKey: ["recognition-types", selectedOrgId],
    queryFn: () => recognitionTypesService.getByOrganization(selectedOrgId, true),
    enabled: !!selectedOrgId,
  });

  // Birthday / Work Anniversary / Celebration are private system types owned by
  // the Important Dates feature — they can't be renamed or deleted and don't
  // belong in this list. The shared endpoint returns them because the Important
  // Dates tab needs them for occasion styling, so filter here rather than
  // varying the request: both tabs cache under the same query key.
  const recognitionTypes = useMemo(
    () => allRecognitionTypes.filter((t) => !t.is_private),
    [allRecognitionTypes]
  );

  // Auto-seed state (useEffect moved after seedMutation definition)
  const [hasAutoSeeded, setHasAutoSeeded] = useState(false);

  // Reset auto-seed flag when org changes
  useEffect(() => {
    setHasAutoSeeded(false);
  }, [selectedOrgId]);

  // Create mutation
  const createMutation = useMutation({
    mutationFn: (data) => recognitionTypesService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["recognition-types", selectedOrgId] });
      toast.success(t("recognition_types.toasts.created"));
      closeModal();
    },
    onError: (error) => toast.error(formatError(error)),
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => recognitionTypesService.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["recognition-types", selectedOrgId] });
      toast.success(t("recognition_types.toasts.updated"));
      closeModal();
    },
    onError: (error) => toast.error(formatError(error)),
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (id) => recognitionTypesService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["recognition-types", selectedOrgId] });
      toast.success(t("recognition_types.toasts.deleted"));
      setDeleteConfirmId(null);
    },
    onError: (error) => {
      toast.error(formatError(error));
      setDeleteConfirmId(null);
    },
  });

  // Seed defaults mutation
  const seedMutation = useMutation({
    mutationFn: () => {
      if (!selectedOrgId) {
        return Promise.reject(new Error(t("recognition_types.errors.no_org_selected")));
      }
      return recognitionTypesService.seedDefaults(selectedOrgId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["recognition-types", selectedOrgId] });
      toast.success(t("recognition_types.toasts.defaults_created"));
    },
    onError: (error) => toast.error(formatError(error)),
  });

  // Auto-seed defaults when no recognition types exist
  useEffect(() => {
    if (
      selectedOrgId &&
      isTypesFetched &&
      !isLoadingTypes &&
      recognitionTypes.length === 0 &&
      !hasAutoSeeded &&
      !seedMutation.isPending
    ) {
      setHasAutoSeeded(true);
      seedMutation.mutate();
    }
  }, [selectedOrgId, isTypesFetched, isLoadingTypes, recognitionTypes.length, hasAutoSeeded, seedMutation]);

  // Reset to defaults mutation
  const resetMutation = useMutation({
    mutationFn: () => {
      if (!selectedOrgId) {
        return Promise.reject(new Error(t("recognition_types.errors.no_org_selected")));
      }
      return recognitionTypesService.resetToDefaults(selectedOrgId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["recognition-types", selectedOrgId] });
      toast.success(t("recognition_types.toasts.reset_done"));
      setShowResetConfirm(false);
    },
    onError: (error) => {
      toast.error(formatError(error));
      setShowResetConfirm(false);
    },
  });

  // Reorder mutation
  const reorderMutation = useMutation({
    mutationFn: (orderedIds) => recognitionTypesService.reorder(selectedOrgId, orderedIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["recognition-types", selectedOrgId] });
      toast.success(t("recognition_types.toasts.order_updated"));
    },
    onError: (error) => toast.error(formatError(error)),
  });

  // Custom icons query
  const {
    data: customIcons = [],
    refetch: refetchCustomIcons,
  } = useQuery({
    queryKey: ["recognition-type-custom-icons", selectedOrgId],
    queryFn: () => recognitionTypesService.getCustomIcons(selectedOrgId),
    enabled: !!selectedOrgId,
  });

  // Upload custom icon mutation
  const uploadIconMutation = useMutation({
    mutationFn: (file) => recognitionTypesService.uploadCustomIcon(selectedOrgId, file),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["recognition-type-custom-icons", selectedOrgId] });
      toast.success(t("recognition_types.editor.icon_uploaded"));
      // Auto-select the newly uploaded icon
      setFormData((prev) => ({ ...prev, icon: data.url }));
    },
    onError: (error) => toast.error(formatError(error)),
  });

  // Delete custom icon mutation
  const deleteIconMutation = useMutation({
    mutationFn: (iconId) => recognitionTypesService.deleteCustomIcon(iconId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["recognition-type-custom-icons", selectedOrgId] });
      toast.success(t("recognition_types.editor.icon_deleted"));
    },
    onError: (error) => toast.error(formatError(error)),
  });

  // Handle custom icon file upload
  const handleIconUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const validTypes = ["image/png", "image/x-icon"];
    const validExtensions = [".png", ".ico"];
    const ext = file.name.toLowerCase().slice(file.name.lastIndexOf("."));

    if (!validTypes.includes(file.type) && !validExtensions.includes(ext)) {
      toast.error(t("recognition_types.editor.invalid_icon_type"));
      return;
    }

    if (file.size > 512 * 1024) {
      toast.error(t("recognition_types.editor.icon_too_large"));
      return;
    }

    uploadIconMutation.mutate(file);
    // Reset the input so the same file can be re-selected
    e.target.value = "";
  };

  const {
    displayItems: displayTypes,
    draggedId: draggedTypeId,
    getRowProps,
    getGripProps,
  } = usePressDragReorder({
    items: recognitionTypes,
    getId: (t) => t.id,
    onReorder: (ordered) => reorderMutation.mutate(ordered.map((t) => t.id)),
  });

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      toast.error(t("recognition_types.toasts.name_required"));
      return;
    }

    // Store colors in from-[#hex] format for Tailwind arbitrary values
    const payload = {
      name: formData.name.trim(),
      value: formData.name.trim(),
      description: formData.description.trim() || null,
      icon: formData.icon,
      color_from: `from-[${formData.color_from}]`,
      color_to: `to-[${formData.color_to}]`,
      organization_id: selectedOrgId,
    };

    if (editingType) {
      updateMutation.mutate({ id: editingType.id, data: payload });
    } else {
      createMutation.mutate(payload);
    }
  };

  // Helper to extract hex from stored format
  const extractHexColor = (colorString) => {
    const match = colorString?.match(/\[(.*?)\]/);
    return match ? match[1] : "#a855f7";
  };

  // Helper to check if an icon value is a custom icon URL
  const isCustomIconUrl = (icon) => icon && icon.startsWith("/api/v1/recognition-type-icons/");

  const openForCreate = () => {
    setEditingType(null);
    setFormData({
      name: "",
      description: "",
      icon: "⭐",
      color_from: "#a855f7",
      color_to: "#3b82f6",
    });
    setShowEmojiPicker(false);
    setPickingColor(null);
    setIconTab("emojis");
    setShowModal(true);
  };

  const openForEdit = (type) => {
    setEditingType(type);
    const icon = type.icon || type.emoji || "⭐";
    setFormData({
      name: type.name,
      description: type.description || "",
      icon: icon,
      color_from: type.color_from ? extractHexColor(type.color_from) : "#a855f7",
      color_to: type.color_to ? extractHexColor(type.color_to) : "#3b82f6",
    });
    setShowEmojiPicker(false);
    setPickingColor(null);
    setIconTab(isCustomIconUrl(icon) ? "custom" : "emojis");
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingType(null);
    setShowEmojiPicker(false);
    setPickingColor(null);
    setIconTab("emojis");
  };

  const handleDelete = (id) => {
    deleteMutation.mutate(id);
  };

  // Handle color selection from palette
  const handleColorSelect = (color) => {
    if (pickingColor === 'from') {
      setFormData({ ...formData, color_from: color });
    } else if (pickingColor === 'to') {
      setFormData({ ...formData, color_to: color });
    }
    setPickingColor(null);
  };

  // Handle gradient preset selection
  const handleGradientPreset = (preset) => {
    setFormData({ ...formData, color_from: preset.from, color_to: preset.to });
    setPickingColor(null);
  };

  return (
    <div className="p-3 sm:p-6 space-y-4 sm:space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-lg sm:text-2xl font-bold text-slate-800">{t("recognition_types.title")}</h1>
        <p className="text-xs sm:text-sm text-slate-600 mt-0.5 sm:mt-1">
          {t("recognition_types.subtitle")}
        </p>
      </div>

      {/* Recognition Types Card */}
      <div className="bg-white rounded-2xl shadow-md p-3 sm:p-6">
        <div className="flex items-center justify-between mb-3 sm:mb-4 gap-2 flex-wrap">
          <h2 className="text-base sm:text-lg font-semibold text-slate-900 flex items-center gap-2 min-w-0">
            <Settings className="w-4 h-4 sm:w-5 sm:h-5 text-purple-600 flex-shrink-0" />
            <span className="truncate">{t("recognition_types.card_title")}</span>
          </h2>
          <div className="flex items-center gap-2 sm:gap-3 flex-wrap flex-shrink-0">
            {isSuperAdmin && (
            <select
              value={selectedOrgId || ""}
              onChange={(e) => setSelectedOrgId(e.target.value)}
              className="px-3 sm:px-4 py-1.5 sm:py-2 border border-slate-300 rounded-full focus:ring-2 focus:ring-purple-500 focus:border-transparent text-xs sm:text-sm max-w-[160px] sm:max-w-none"
              disabled={isLoadingOrgs}
            >
              {isLoadingOrgs ? (
                <option>{t("recognition_types.org_loading")}</option>
              ) : organizations.length === 0 ? (
                <option>{t("recognition_types.no_organizations")}</option>
              ) : (
                organizations.map((org) => (
                  <option key={org.id} value={org.id}>
                    {org.name}
                  </option>
                ))
              )}
            </select>
            )}
            <button
              onClick={() => refetchTypes()}
              disabled={!selectedOrgId || isLoadingTypes}
              className="p-1.5 sm:p-2 text-slate-600 hover:text-purple-600 hover:bg-purple-50 rounded-full transition-colors disabled:opacity-50"
              title={t("recognition_types.refresh")}
            >
              <RefreshCw className={`w-4 h-4 sm:w-5 sm:h-5 ${isLoadingTypes ? "animate-spin" : ""}`} />
            </button>
          </div>
        </div>

        <p className="text-xs sm:text-sm text-slate-500 mb-4 sm:mb-6">
          {t("recognition_types.description")}
        </p>

        {/* Recognition Types List */}
        {isLoadingTypes || seedMutation.isPending ? (
          <div className="flex justify-center py-12">
            <div className="w-8 h-8 border-4 border-purple-200 border-t-purple-600 rounded-full animate-spin" />
          </div>
        ) : (
          <div className="space-y-3">
            {displayTypes.map((type, displayIndex) => {
              // Handle both old format (color_from/color_to) and new format (no colors)
              const hasColors = type.color_from && type.color_to;
              const isCustom = hasColors && type.color_from.includes("[");
              const gradientStyle = isCustom
                ? { background: `linear-gradient(to right, ${type.color_from.match(/\[(.*?)\]/)?.[1]}, ${type.color_to.match(/\[(.*?)\]/)?.[1]})` }
                : !hasColors
                ? { background: 'linear-gradient(to right, #a855f7, #3b82f6)' }  // Default gradient
                : {};
              const gradientClass = isCustom || !hasColors ? "" : `bg-gradient-to-r ${type.color_from} ${type.color_to}`;
              // Handle both "icon" and "emoji" field names
              const displayIcon = type.icon || type.emoji || "⭐";
              const isDragged = draggedTypeId != null && type.id === draggedTypeId;
              const originalIndex = recognitionTypes.findIndex((t) => t.id === type.id);

              return (
                <div
                  key={type.id}
                  {...getRowProps(originalIndex, displayIndex)}
                  className={`flex items-center justify-between gap-2 p-2 sm:p-4 rounded-2xl border bg-white select-none transition-all ${
                    isDragged
                      ? "opacity-90 shadow-xl border-purple-500 ring-2 ring-purple-300 z-10 relative"
                      : "border-slate-200"
                  }`}
                >
                  <div className="flex items-center gap-1.5 sm:gap-3 min-w-0 flex-1">
                    <div
                      {...getGripProps(originalIndex)}
                      className="cursor-grab active:cursor-grabbing text-slate-400 hover:text-slate-600 flex-shrink-0 p-2 -m-2"
                    >
                      <GripVertical className="w-4 h-4 sm:w-5 sm:h-5" />
                    </div>
                    <span className="hidden sm:block text-sm font-medium text-slate-400 w-6 text-center">{displayIndex + 1}</span>
                    <div
                      className={`w-9 h-9 sm:w-12 sm:h-12 rounded-2xl ${gradientClass} flex items-center justify-center text-base sm:text-2xl shadow-md flex-shrink-0`}
                      style={gradientStyle}
                    >
                      {isCustomIconUrl(displayIcon) ? (
                        <img src={displayIcon} alt="" className="w-5 h-5 sm:w-7 sm:h-7 object-contain" />
                      ) : (
                        displayIcon
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm sm:text-base font-medium text-slate-900 truncate">{type.name}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-0.5 sm:gap-2 flex-shrink-0">
                    {deleteConfirmId === type.id ? (
                      <>
                        <button
                          onClick={() => handleDelete(type.id)}
                          disabled={deleteMutation.isPending}
                          className="p-1.5 sm:p-2 text-red-600 hover:bg-red-50 rounded-full transition-colors"
                          title={t("recognition_types.confirm_delete")}
                        >
                          <Check className="w-4 h-4 sm:w-5 sm:h-5" />
                        </button>
                        <button
                          onClick={() => setDeleteConfirmId(null)}
                          className="p-1.5 sm:p-2 text-slate-600 hover:bg-slate-100 rounded-full transition-colors"
                          title={t("recognition_types.cancel")}
                        >
                          <X className="w-4 h-4 sm:w-5 sm:h-5" />
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          onClick={() => openForEdit(type)}
                          className="p-1.5 sm:p-2 text-slate-600 hover:text-purple-600 hover:bg-purple-50 rounded-full transition-colors"
                          title={t("recognition_types.edit")}
                        >
                          <Edit2 className="w-4 h-4 sm:w-5 sm:h-5" />
                        </button>
                        <button
                          onClick={() => setDeleteConfirmId(type.id)}
                          className="p-1.5 sm:p-2 text-slate-600 hover:text-red-600 hover:bg-red-50 rounded-full transition-colors"
                          title={t("recognition_types.delete")}
                        >
                          <Trash2 className="w-4 h-4 sm:w-5 sm:h-5" />
                        </button>
                      </>
                    )}
                  </div>
                </div>
              );
            })}

            {/* Add New Button */}
            {recognitionTypes.length < 18 && (
              <button
                onClick={openForCreate}
                className="w-full p-2.5 sm:p-4 rounded-full border-2 border-dashed border-slate-300 text-slate-600 hover:border-purple-400 hover:text-purple-600 hover:bg-purple-50 transition-colors flex items-center justify-center gap-1.5 sm:gap-2 text-xs sm:text-base whitespace-nowrap"
              >
                <Plus className="w-4 h-4 sm:w-5 sm:h-5" />
                {t("recognition_types.add_type")}
              </button>
            )}

            {recognitionTypes.length >= 18 && (
              <div className="flex items-center gap-2 p-2 sm:p-3 bg-amber-50 border border-amber-200 rounded-2xl text-amber-700 text-xs sm:text-sm">
                <AlertCircle className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" />
                {t("recognition_types.max_reached")}
              </div>
            )}

            {/* Reset to Defaults Button - at bottom */}
            <div className="pt-3 mt-3 sm:pt-4 sm:mt-4 border-t border-slate-200">
              <button
                onClick={() => setShowResetConfirm(true)}
                disabled={resetMutation.isPending}
                className="flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm text-slate-600 hover:text-orange-600 hover:bg-orange-50 rounded-full transition-colors border border-slate-200 whitespace-nowrap"
              >
                <RotateCcw className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                {t("recognition_types.reset_defaults")}
              </button>
              <p className="text-[11px] sm:text-xs text-slate-500 mt-2">
                {t("recognition_types.reset_hint")}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Reset Confirmation Modal */}
      {showResetConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-3 bg-orange-100 rounded-full">
                <RotateCcw className="w-6 h-6 text-orange-600" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900">{t("recognition_types.reset_modal.title")}</h3>
            </div>
            <p className="text-slate-600 mb-6">
              {t("recognition_types.reset_modal.body")}
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowResetConfirm(false)}
                className="flex-1 px-4 py-2 border border-slate-300 text-slate-700 rounded-full hover:bg-slate-50 transition-colors"
              >
                {t("common:actions.cancel")}
              </button>
              <button
                onClick={() => resetMutation.mutate()}
                disabled={resetMutation.isPending}
                className="flex-1 px-4 py-2 bg-orange-600 text-white rounded-full hover:bg-orange-700 transition-colors disabled:opacity-50"
              >
                {resetMutation.isPending ? t("recognition_types.reset_modal.resetting") : t("recognition_types.reset_modal.reset")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-3 sm:p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="p-3 sm:p-6 border-b border-slate-200">
              <h3 className="text-base sm:text-lg font-semibold text-slate-900">
                {editingType ? t("recognition_types.modal.edit_title") : t("recognition_types.modal.new_title")}
              </h3>
            </div>

            <form onSubmit={handleSubmit} className="p-3 sm:p-6 space-y-4 sm:space-y-6">
              {/* Name */}
              <div>
                <label className="block text-xs sm:text-sm font-medium text-slate-700 mb-1.5 sm:mb-2">
                  {t("recognition_types.modal.name_label")}
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder={t("recognition_types.modal.name_placeholder")}
                  className="w-full px-3 sm:px-4 py-1.5 sm:py-2 text-sm sm:text-base border border-slate-300 rounded-full focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  maxLength={100}
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-xs sm:text-sm font-medium text-slate-700 mb-1.5 sm:mb-2">
                  {t("recognition_types.modal.description_label")}
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder={t("recognition_types.modal.description_placeholder")}
                  rows={2}
                  maxLength={255}
                  className="w-full px-3 sm:px-4 py-1.5 sm:py-2 text-sm sm:text-base border border-slate-300 rounded-2xl focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
                />
                <p className="text-[11px] sm:text-xs text-slate-400 text-right mt-1">
                  {formData.description.length}/255
                </p>
              </div>

              {/* Icon Picker */}
              <div>
                <label className="text-xs sm:text-sm font-medium text-slate-700 mb-1.5 sm:mb-2 block">{t("recognition_types.editor.icon_label")}</label>

                {/* Tab Buttons */}
                <div className="flex gap-2 mb-2 sm:mb-3">
                  <button
                    type="button"
                    onClick={() => setIconTab("emojis")}
                    className={`flex items-center gap-1.5 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-medium transition-all whitespace-nowrap ${
                      iconTab === "emojis"
                        ? "bg-purple-100 text-purple-700 ring-2 ring-purple-500"
                        : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                    }`}
                  >
                    <Smile className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                    {t("recognition_types.editor.icons_tab")}
                  </button>
                  <button
                    type="button"
                    onClick={() => { setIconTab("custom"); refetchCustomIcons(); }}
                    className={`flex items-center gap-1.5 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-medium transition-all whitespace-nowrap ${
                      iconTab === "custom"
                        ? "bg-purple-100 text-purple-700 ring-2 ring-purple-500"
                        : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                    }`}
                  >
                    <Image className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                    {t("recognition_types.editor.custom_icons_tab")}
                  </button>
                </div>

                {/* Emojis Tab */}
                {iconTab === "emojis" && (
                  <div>
                    {/* Quick Emojis — 8 per row on mobile, aligned via aspect-square cells */}
                    <div className="grid grid-cols-8 gap-1 sm:gap-2 mb-2">
                      {QUICK_EMOJIS.map((emoji) => (
                        <button
                          key={emoji}
                          type="button"
                          onClick={() => setFormData({ ...formData, icon: emoji })}
                          className={`aspect-square flex items-center justify-center text-lg sm:text-2xl rounded-full transition-all ${
                            formData.icon === emoji
                              ? "bg-purple-100 ring-2 ring-purple-500"
                              : "hover:bg-slate-100"
                          }`}
                        >
                          {emoji}
                        </button>
                      ))}
                    </div>

                    {/* More Emojis Toggle */}
                    <button
                      type="button"
                      onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                      className="flex items-center gap-1 text-xs sm:text-sm text-purple-600 hover:text-purple-700 mb-2"
                    >
                      <Smile className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                      {showEmojiPicker ? t("recognition_types.editor.show_less") : t("recognition_types.editor.more_emojis")}
                    </button>

                    {/* Extended Emoji Picker */}
                    {showEmojiPicker && (
                      <div className="border border-slate-200 rounded-2xl p-2 sm:p-3 mt-2 max-h-60 overflow-y-auto">
                        {Object.entries(EMOJI_CATEGORIES).map(([category, emojis]) => (
                          <div key={category} className="mb-3">
                            <div className="text-[11px] sm:text-xs font-medium text-slate-500 mb-1">{emojiCategoryLabels[category] || category}</div>
                            <div className="grid grid-cols-8 gap-1">
                              {emojis.map((emoji) => (
                                <button
                                  key={`${category}-${emoji}`}
                                  type="button"
                                  onClick={() => setFormData({ ...formData, icon: emoji })}
                                  className={`aspect-square flex items-center justify-center text-base sm:text-xl rounded transition-all ${
                                    formData.icon === emoji
                                      ? "bg-purple-100 ring-2 ring-purple-500"
                                      : "hover:bg-slate-100"
                                  }`}
                                >
                                  {emoji}
                                </button>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Custom emoji input */}
                    <div className="mt-2">
                      <input
                        type="text"
                        value={isCustomIconUrl(formData.icon) ? "" : formData.icon}
                        onChange={(e) => setFormData({ ...formData, icon: e.target.value.slice(0, 2) })}
                        placeholder={t("recognition_types.editor.emoji_placeholder")}
                        className="w-full px-3 sm:px-4 py-1.5 sm:py-2 border border-slate-300 rounded-full focus:ring-2 focus:ring-purple-500 focus:border-transparent text-center text-lg sm:text-2xl"
                        maxLength={2}
                      />
                    </div>
                  </div>
                )}

                {/* Custom Icons Tab */}
                {iconTab === "custom" && (
                  <div>
                    {/* Upload Button */}
                    <label className="flex items-center justify-center gap-2 w-full p-3 mb-3 border-2 border-dashed border-slate-300 rounded-2xl text-slate-600 hover:border-purple-400 hover:text-purple-600 hover:bg-purple-50 transition-colors cursor-pointer">
                      <Upload className="w-5 h-5" />
                      <span className="text-sm font-medium">
                        {uploadIconMutation.isPending ? t("recognition_types.editor.uploading") : t("recognition_types.editor.upload_custom_icon")}
                      </span>
                      <input
                        type="file"
                        accept=".png,.ico"
                        onChange={handleIconUpload}
                        disabled={uploadIconMutation.isPending}
                        className="hidden"
                      />
                    </label>
                    <p className="text-xs text-slate-500 mb-3">{t("recognition_types.editor.format_hint")}</p>

                    {/* Custom Icons Gallery */}
                    {customIcons.length > 0 ? (
                      <div className="grid grid-cols-6 gap-2">
                        {customIcons.map((icon) => (
                          <div key={icon.id} className="relative group">
                            <button
                              type="button"
                              onClick={() => setFormData({ ...formData, icon: icon.url })}
                              className={`w-full aspect-square rounded-xl border-2 p-1.5 flex items-center justify-center transition-all ${
                                formData.icon === icon.url
                                  ? "border-purple-500 bg-purple-50 ring-2 ring-purple-200"
                                  : "border-slate-200 hover:border-slate-300 hover:bg-slate-50"
                              }`}
                              title={icon.filename}
                            >
                              <img
                                src={icon.url}
                                alt={icon.filename}
                                className="w-8 h-8 object-contain"
                              />
                            </button>
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                if (formData.icon === icon.url) {
                                  setFormData({ ...formData, icon: "⭐" });
                                }
                                deleteIconMutation.mutate(icon.id);
                              }}
                              className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity text-xs"
                              title={t("recognition_types.editor.delete_icon")}
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-6 text-slate-400 text-sm">
                        {t("recognition_types.editor.no_custom_icons")}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Color Picker */}
              <div>
                <label className="block text-xs sm:text-sm font-medium text-slate-700 mb-2 sm:mb-3">{t("recognition_types.editor.gradient_label")}</label>

                {/* Current Gradient Preview with clickable selectors */}
                <div className="mb-3 sm:mb-4">
                  <div className="flex items-center gap-2 sm:gap-3 mb-2">
                    {/* Start Color Selector */}
                    <button
                      type="button"
                      onClick={() => setPickingColor(pickingColor === 'from' ? null : 'from')}
                      className={`flex-1 min-w-0 p-2 sm:p-3 rounded-full border-2 transition-all ${
                        pickingColor === 'from'
                          ? 'border-purple-500 ring-2 ring-purple-200'
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      <div className="flex items-center gap-1.5 sm:gap-2 min-w-0">
                        <div
                          className="w-6 h-6 sm:w-8 sm:h-8 rounded-2xl shadow-inner border border-slate-200 flex-shrink-0"
                          style={{ backgroundColor: formData.color_from }}
                        />
                        <div className="text-left min-w-0 flex-1">
                          <div className="text-[10px] sm:text-xs text-slate-500 truncate">{t("recognition_types.editor.start")}</div>
                          <div className="text-[11px] sm:text-sm font-medium text-slate-700 whitespace-nowrap truncate">
                            {pickingColor === 'from' ? t("recognition_types.editor.pick_below") : t("recognition_types.editor.click_to_change")}
                          </div>
                        </div>
                      </div>
                    </button>

                    {/* Arrow */}
                    <div className="text-slate-400 flex-shrink-0">→</div>

                    {/* End Color Selector */}
                    <button
                      type="button"
                      onClick={() => setPickingColor(pickingColor === 'to' ? null : 'to')}
                      className={`flex-1 min-w-0 p-2 sm:p-3 rounded-full border-2 transition-all ${
                        pickingColor === 'to'
                          ? 'border-purple-500 ring-2 ring-purple-200'
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      <div className="flex items-center gap-1.5 sm:gap-2 min-w-0">
                        <div
                          className="w-6 h-6 sm:w-8 sm:h-8 rounded-2xl shadow-inner border border-slate-200 flex-shrink-0"
                          style={{ backgroundColor: formData.color_to }}
                        />
                        <div className="text-left min-w-0 flex-1">
                          <div className="text-[10px] sm:text-xs text-slate-500 truncate">{t("recognition_types.editor.end")}</div>
                          <div className="text-[11px] sm:text-sm font-medium text-slate-700 whitespace-nowrap truncate">
                            {pickingColor === 'to' ? t("recognition_types.editor.pick_below") : t("recognition_types.editor.click_to_change")}
                          </div>
                        </div>
                      </div>
                    </button>
                  </div>

                  {/* Gradient Preview Bar */}
                  <div
                    className="h-8 sm:h-10 rounded-2xl shadow-inner border border-slate-200"
                    style={{ background: `linear-gradient(to right, ${formData.color_from}, ${formData.color_to})` }}
                  />
                </div>

                {/* Color Palette (shown when picking a color) */}
                {pickingColor && (
                  <div className="mb-4 p-3 border border-purple-200 rounded-2xl bg-purple-50/50">
                    <div className="text-xs font-medium text-purple-700 mb-2">
                      {pickingColor === 'from' ? t("recognition_types.editor.select_start_color") : t("recognition_types.editor.select_end_color")}
                    </div>
                    {/* Eyedropper + Hex input */}
                    <div className="flex items-center gap-2 mb-3">
                      <label className="relative w-10 h-10 flex-shrink-0 cursor-pointer" title={t("recognition_types.editor.eyedropper_title")}>
                        <div
                          className="w-10 h-10 rounded-full border-2 border-slate-200 shadow-inner"
                          style={{ backgroundColor: pickingColor === 'from' ? formData.color_from : formData.color_to }}
                        />
                        <input
                          type="color"
                          value={pickingColor === 'from' ? formData.color_from : formData.color_to}
                          onChange={(e) => {
                            const c = e.target.value;
                            if (pickingColor === 'from') setFormData({ ...formData, color_from: c });
                            else setFormData({ ...formData, color_to: c });
                          }}
                          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                        />
                      </label>
                      <input
                        type="text"
                        key={`${pickingColor}-${pickingColor === 'from' ? formData.color_from : formData.color_to}`}
                        defaultValue={pickingColor === 'from' ? formData.color_from : formData.color_to}
                        onChange={(e) => {
                          let val = e.target.value.trim();
                          if (!val.startsWith('#')) val = '#' + val;
                          if (/^#[0-9A-Fa-f]{6}$/.test(val)) {
                            if (pickingColor === 'from') setFormData({ ...formData, color_from: val });
                            else setFormData({ ...formData, color_to: val });
                          }
                        }}
                        className="flex-1 px-3 py-2 border border-slate-200 rounded-xl text-sm font-mono focus:outline-none focus:ring-2 focus:ring-purple-300 focus:border-purple-400"
                        placeholder="#000000"
                        maxLength={7}
                      />
                    </div>
                    <div className="space-y-1">
                      {COLOR_PALETTE.map((row, rowIndex) => (
                        <div key={rowIndex} className="flex gap-1">
                          {row.map((color) => (
                            <button
                              key={color}
                              type="button"
                              onClick={() => handleColorSelect(color)}
                              className={`flex-1 h-7 rounded transition-all hover:scale-110 hover:z-10 ${
                                (pickingColor === 'from' && formData.color_from === color) ||
                                (pickingColor === 'to' && formData.color_to === color)
                                  ? 'ring-2 ring-purple-500 ring-offset-1'
                                  : ''
                              }`}
                              style={{ backgroundColor: color }}
                              title={color}
                            />
                          ))}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Gradient Presets */}
                <div>
                  <div className="text-xs font-medium text-slate-500 mb-2">{t("recognition_types.editor.quick_presets")}</div>
                  <div className="grid grid-cols-4 gap-2">
                    {GRADIENT_PRESETS.map((preset, index) => (
                      <button
                        key={index}
                        type="button"
                        onClick={() => handleGradientPreset(preset)}
                        className={`h-10 rounded-full transition-all ${
                          formData.color_from === preset.from && formData.color_to === preset.to
                            ? 'ring-2 ring-offset-2 ring-purple-500'
                            : 'hover:scale-105'
                        }`}
                        style={{ background: `linear-gradient(to right, ${preset.from}, ${preset.to})` }}
                        title={presetLabels[preset.id] || preset.id}
                      />
                    ))}
                  </div>
                </div>
              </div>

              {/* Preview */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  {t("recognition_types.editor.preview")}
                </label>
                <div className="flex items-center gap-3 p-4 bg-slate-50 rounded-2xl">
                  <div
                    className="w-12 h-12 rounded-2xl flex items-center justify-center text-2xl shadow-md"
                    style={{ background: `linear-gradient(to right, ${formData.color_from}, ${formData.color_to})` }}
                  >
                    {isCustomIconUrl(formData.icon) ? (
                      <img src={formData.icon} alt="" className="w-7 h-7 object-contain" />
                    ) : (
                      formData.icon
                    )}
                  </div>
                  <div className="font-medium text-slate-900">
                    {formData.name || t("recognition_types.modal.preview_default_name")}
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-2 sm:gap-3 pt-3 sm:pt-4">
                <button
                  type="button"
                  onClick={closeModal}
                  className="flex-1 px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm border border-slate-300 text-slate-700 rounded-full hover:bg-slate-50 transition-colors whitespace-nowrap"
                >
                  {t("common:actions.cancel")}
                </button>
                <button
                  type="submit"
                  disabled={createMutation.isPending || updateMutation.isPending}
                  className="flex-1 px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm org-bg-primary org-btn text-white rounded-full transition-colors disabled:opacity-50 whitespace-nowrap"
                >
                  {createMutation.isPending || updateMutation.isPending
                    ? t("recognition_types.editor.saving")
                    : editingType
                    ? t("common:actions.update")
                    : t("common:actions.create")}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default RecognitionTypesSection;
